// Encart « peut-on construire le tableau d'adoption ? ».
//
// Le verdict et les quatre socles viennent du SERVEUR, qui compte la base
// réelle. L'ancien frontend comptait en parallèle côté client, avec les données
// de démonstration : on lisait « Matière insuffisante · insights 3/5 » empilé
// sur « La matière est suffisante · 5/5 » dans le même écran. Une règle produit
// n'a qu'une source de vérité, et c'est celle qui refusera le calcul.
import type { AdoptionReadiness } from '../api/types';
import { ApiRequestError } from '../api/client';
import { Button, Eyebrow, Note } from '../components/ui';

interface AdoptionPanelProps {
  readiness: AdoptionReadiness;
  computedAt: string | null;
  onBuild: () => void;
  building: boolean;
  error: unknown;
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleString('fr-FR', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function AdoptionPanel({
  readiness,
  computedAt,
  onBuild,
  building,
  error,
}: AdoptionPanelProps) {
  const { ready, checks, progress, missingLabel, unsupportedInsights } = readiness;
  // Un projet à modèle n'a qu'un seuil : deux piliers notés.
  const byModel = checks[0]?.key === 'pillars';

  const buildLabel = building
    ? 'Calcul en cours…'
    : computedAt
      ? 'Recalculer le tableau d’adoption'
      : 'Prêt pour construire le tableau d’adoption';

  return (
    <section className="zy-card adoption">
      <div className="adoption-head">
        <div className="adoption-verdict">
          <Eyebrow>Tableau d’adoption</Eyebrow>
          {byModel ? (
            <>
              <h2 className="t-h3">{missingLabel ? 'Pas encore de score global' : 'Le score suit vos mesures'}</h2>
              <p className="card-lede">
                {missingLabel
                  ? 'Le score global demande au moins deux piliers notés. Clôturez un baromètre pour les obtenir.'
                  : 'Chaque baromètre clos met le score à jour. Recalculez après avoir ajouté une mesure ou une preuve.'}
              </p>
            </>
          ) : ready ? (
            <>
              <h2 className="t-h3">La matière est suffisante — le tableau peut être construit</h2>
              <p className="card-lede">
                Les quatre socles sont atteints. Le score sera calculé sur des faits, des mesures
                et des insights réels.
              </p>
            </>
          ) : (
            <>
              <h2 className="t-h3">Pas encore constructible</h2>
              <p className="card-lede">
                Il manque : <strong>{missingLabel}</strong>. Un score calculé sur trop peu de
                matière donnerait une fausse assurance.
              </p>
            </>
          )}
          {computedAt ? (
            <p className="adoption-computed t-mono">Dernier calcul : {formatDate(computedAt)}</p>
          ) : null}
          <progress className="zy-progress" value={progress} max={100} aria-hidden="true" />
        </div>

        {ready ? (
          <Button variant="accent" onClick={onBuild} loading={building}>
            {buildLabel}
          </Button>
        ) : null}
      </div>

      <ul className="checks">
        {checks.map((c) => (
          <li key={c.key} className={`check${c.ok ? ' check-ok' : ''}`}>
            <span className="check-dot" aria-hidden="true">
              {c.ok ? '✓' : ''}
            </span>
            <span className="check-body">
              <span className="check-label">{c.label}</span>
              <span className="check-hint">{c.hint}</span>
            </span>
            <span className="check-count">
              {c.have} / {c.need}
            </span>
          </li>
        ))}
      </ul>

      {unsupportedInsights > 0 ? (
        <Note tone="avertissement">
          <strong>{unsupportedInsights}</strong> insight(s) ne citent aucun fait. Le tableau serait
          construit sur des opinions — rattachez-leur leurs preuves avant de vous appuyer dessus.
        </Note>
      ) : null}

      {error ? (
        <Note tone="erreur" role="alert">
          {error instanceof ApiRequestError ? error.message : 'Le calcul a échoué.'}
        </Note>
      ) : null}
    </section>
  );
}
